import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Zap, Crown, Moon } from "lucide-react";
import { startCheckout } from "@/lib/payments";

const LIMIT_EVENT = "moyo:limit";
export const openUpgradeSheet = (detail = {}) => window.dispatchEvent(new CustomEvent(LIMIT_EVENT, { detail }));

const FALLBACK = "You've crushed your 3 free Vibe Checks for today! Upgrade to Clapham Speed Pass for unlimited daily calls, or grab a 5 Speed Token Pack to keep matching tonight.";

export function UpgradeSheet() {
  const [open, setOpen] = useState(false);
  const [info, setInfo] = useState({});
  const [busy, setBusy] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const onLimit = (e) => { setInfo(e.detail || {}); setOpen(true); };
    window.addEventListener(LIMIT_EVENT, onLimit);
    return () => window.removeEventListener(LIMIT_EVENT, onLimit);
  }, []);


  const buyPass = async () => { setBusy("pass"); try { await startCheckout("speed_pass"); } catch { setBusy(null); } };
  const buyPack = async () => { setBusy("pack"); try { await startCheckout("vibe_pack"); } catch { setBusy(null); } };

  const usage = info.usage || {};
  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent side="bottom" data-testid="upgrade-sheet" className="rounded-t-3xl border-t border-amber-500/30 bg-[#0b130e]/95 backdrop-blur-xl max-w-xl mx-auto px-6 pb-[max(1.5rem,env(safe-area-inset-bottom))]">
        <div className="mx-auto h-1.5 w-12 rounded-full bg-white/20 mb-4" />
        <SheetHeader className="text-left">
          <SheetTitle data-testid="upgrade-sheet-title" className="font-display font-black text-2xl">{info.title || "Daily limit reached"}</SheetTitle>
          <SheetDescription data-testid="upgrade-sheet-message" className="text-slate-300 text-sm leading-relaxed">{info.message || FALLBACK}</SheetDescription>
        </SheetHeader>
        {usage.calls_limit != null && (
          <p data-testid="upgrade-sheet-usage" className="text-xs text-slate-400 mt-3">
            Today: {usage.daily_vibe_calls}/{usage.calls_limit} vibe checks{usage.messages_limit != null ? ` · ${usage.daily_messages}/${usage.messages_limit} messages` : ""}
            {usage.trial_active ? ` · free trial, ${usage.trial_days_left} days left` : ""}
          </p>
        )}
        <div className="grid gap-3 mt-5">
          <button data-testid="upgrade-speed-pass" onClick={buyPass} disabled={!!busy}
            className="rounded-2xl p-4 text-left btn-gold flex items-center gap-3 disabled:opacity-60 transition-transform active:scale-[0.98]">
            <Crown className="h-6 w-6 shrink-0" />
            <span className="flex-1"><span className="font-display font-black block">Clapham Speed Pass</span><span className="text-xs opacity-80">Unlimited daily calls & messages + free contact cards · £12.99/month</span></span>
            <span className="text-[10px] font-bold uppercase tracking-wider rounded-full bg-black/20 px-2 py-0.5">Monthly</span>
          </button>
          <button data-testid="upgrade-vibe-pack" onClick={buyPack} disabled={!!busy}
            className="rounded-2xl p-4 text-left glass border-pink-400/40 hover:border-pink-400/70 flex items-center gap-3 disabled:opacity-60 transition-colors">
            <Zap className="h-6 w-6 text-pink-300 shrink-0" />
            <span className="flex-1"><span className="font-display font-bold block">5 Speed Token Pack</span><span className="text-xs text-slate-400">Each token = 1 extra vibe check + 10 messages, or 1 contact card swap · £1.99</span></span>
            <span className="text-[10px] font-bold uppercase tracking-wider rounded-full bg-white/10 px-2 py-0.5">One-off</span>
          </button>
        </div>
        <div className="flex items-center justify-between mt-5 text-xs text-slate-400">
          <span className="flex items-center gap-1.5"><Moon className="h-3.5 w-3.5" /> Free allowance resets at midnight (London)</span>
          <button data-testid="upgrade-sheet-dismiss" onClick={() => { setOpen(false); if (info.reason === "calls" && window.location.pathname.startsWith("/call/")) navigate("/discover"); }} className="text-slate-300 hover:text-white underline-offset-4 hover:underline">Maybe later</button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
